import React, { useContext, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  Image,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';

import { AppContext } from '../../../App';
import { uploadVerificationDocument } from '../../api/verificationApi';
import { useFormNavigation } from '../../utils/navigationUtils';
import Header from '../../components/Header';
import { useTranslation } from '../../i18n';
import { useFileValidation } from '../../hooks/useFileValidation';
import FileSizeErrorModal from '../../components/FileSizeErrorModal';

const COLORS = {
  primary: '#7C3AED',
  background: '#F7F9FA',
  card: '#FFFFFF',
  textDark: '#1C1C1E',
  textMuted: '#6B7280',
  error: '#DC2626',
  border: '#E5E7EB',
};

export default function TwoSidedDocumentUploadScreen({
  navigation,
  routeName,
  previousRoute,
  nextRoute,
  docType,
  docKey,
  skipFlag,
  title,
  subtitle,
}) {
  const { verification, setVerification, setFormProgress } = useContext(AppContext);
  const { goToNextStep, updateCurrentStep } = useFormNavigation();
  const { t } = useTranslation();
  const { validateFile, formatFileSize } = useFileValidation();

  const saved = verification?.documents?.[docKey] || {};

  const [frontFiles, setFrontFiles] = useState(saved.front || []);
  const [backFiles, setBackFiles] = useState(saved.back || []);
  const [pdfFiles, setPdfFiles] = useState(saved.pdf || []);
  const [skip, setSkip] = useState(Boolean(verification?.documents?.[skipFlag]));

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const [showFileErrorModal, setShowFileErrorModal] = useState(false);
  const [invalidFile, setInvalidFile] = useState(null);
  const [fileErrorMessage, setFileErrorMessage] = useState('');

  useEffect(() => {
    updateCurrentStep(routeName);
  }, []);

  useEffect(() => {
    setVerification((prev) => ({
      ...prev,
      documents: {
        ...(prev?.documents || {}),
        [docKey]: {
          front: frontFiles,
          back: backFiles,
          pdf: pdfFiles,
        },
        [skipFlag]: skip,
      },
    }));
  }, [frontFiles, backFiles, pdfFiles, skip]);

  function validateAndAddFile(file) {
    const validation = validateFile(file);

    if (!validation.isValid) {
      const firstError = validation.errors[0];

      setInvalidFile({
        name: file.name,
        size: file.size ? formatFileSize(file.size) : null,
      });

      setFileErrorMessage(firstError.message);
      setShowFileErrorModal(true);
      return false;
    }

    return true;
  }

  async function takePhoto(side) {
    setError(null);

    const perm = await ImagePicker.requestCameraPermissionsAsync();

    if (!perm.granted) {
      setError(t('documentUpload.cameraPermissionDenied'));
      return;
    }

    const result = await ImagePicker.launchCameraAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.6,
      allowsEditing: true,
    });

    if (!result.canceled && result.assets?.[0]) {
      const a = result.assets[0];

      const file = {
        uri: a.uri,
        name: `${docType}_${side}.jpg`,
        mimeType: 'image/jpeg',
        type: 'image/jpeg',
        size: a.fileSize || 0,
      };

      if (!validateAndAddFile(file)) return;

      if (side === 'front') setFrontFiles([file]);
      else setBackFiles([file]);

      setPdfFiles([]);
      setSkip(false);
    }
  }

  async function pickPdfOrImage() {
    setError(null);

    const result = await DocumentPicker.getDocumentAsync({
      type: ['application/pdf', 'image/*'],
      copyToCacheDirectory: true,
      multiple: false,
    });

    if (result.canceled) return;

    const a = result.assets?.[0];
    if (!a) return;

    const file = {
      uri: a.uri,
      name: a.name || `${docType}_document`,
      mimeType: a.mimeType || 'application/octet-stream',
      type: a.mimeType || 'application/octet-stream',
      size: a.size || 0,
    };

    if (!validateAndAddFile(file)) return;

    if (file.mimeType === 'application/pdf') {
      setPdfFiles([file]);
      setFrontFiles([]);
      setBackFiles([]);
    } else {
      setError(
        'Pour une image importée, veuillez fournir séparément le recto et le verso avec les boutons photo.'
      );
      return;
    }

    setSkip(false);
  }

  function removeFile(kind) {
    if (kind === 'front') setFrontFiles([]);
    if (kind === 'back') setBackFiles([]);
    if (kind === 'pdf') setPdfFiles([]);
  }

  function hasCompleteDocument() {
    if (skip) return true;
    if (pdfFiles.length > 0) return true;
    return frontFiles.length > 0 && backFiles.length > 0;
  }

  async function handleNext() {
    if (!verification.verificationId) {
      setError(t('documentUpload.noVerificationFound'));
      return;
    }

    if (skip) {
      setFormProgress((prev) => ({
        ...prev,
        completedSteps: [...new Set([...(prev.completedSteps || []), routeName])],
        currentStep: nextRoute,
      }));

      setVerification((prev) => ({
        ...prev,
        documents: {
          ...(prev?.documents || {}),
          [docKey]: { front: [], back: [], pdf: [] },
          [skipFlag]: true,
        },
      }));

      goToNextStep(nextRoute);
      return;
    }

    if (!hasCompleteDocument()) {
      setError('Veuillez ajouter un PDF complet ou prendre le recto et le verso.');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      for (const f of frontFiles) {
        await uploadVerificationDocument(verification.verificationId, docType, f, 'front');
      }

      for (const f of backFiles) {
        await uploadVerificationDocument(verification.verificationId, docType, f, 'back');
      }

      for (const f of pdfFiles) {
        await uploadVerificationDocument(verification.verificationId, docType, f, 'pdf');
      }

      setFormProgress((prev) => ({
        ...prev,
        completedSteps: [...new Set([...(prev.completedSteps || []), routeName])],
        currentStep: nextRoute,
      }));

      goToNextStep(nextRoute);
    } catch (e) {
      console.log('Upload error:', e);
      setError(t('documentUpload.uploadError'));
    } finally {
      setUploading(false);
    }
  }

  function FileRow({ file, kind }) {
    if (!file) return null;

    return (
      <View style={styles.fileRow}>
        <View style={{ flex: 1 }}>
          <Text style={styles.fileName} numberOfLines={1}>
            {file.name}
          </Text>
          {file.size > 0 && (
            <Text style={styles.fileSize}>{formatFileSize(file.size)}</Text>
          )}
        </View>

        <Pressable onPress={() => removeFile(kind)}>
          <Text style={styles.remove}>{t('documentUpload.remove')}</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <Header />

      <FileSizeErrorModal
        visible={showFileErrorModal}
        onDismiss={() => setShowFileErrorModal(false)}
        errorMessage={fileErrorMessage}
        fileName={invalidFile?.name}
        fileSize={invalidFile?.size}
        onRetry={() => {
          setShowFileErrorModal(false);
          setInvalidFile(null);
        }}
      />

      <ScrollView contentContainerStyle={styles.scroll}>
        <Pressable
          style={styles.backButton}
          onPress={() => navigation.navigate(previousRoute)}
        >
          <Text style={styles.backIcon}>←</Text>
        </Pressable>

        <Text style={styles.title}>{title}</Text>
        <Text style={styles.subtitle}>{subtitle}</Text>

        <View style={styles.card}>
          <Pressable style={styles.uploadOption} onPress={() => takePhoto('front')}>
            <Image source={require('../../../assets/camera.png')} style={styles.icon} />
            <View>
              <Text style={styles.optionTitle}>Prendre le recto</Text>
              <Text style={styles.optionHint}>Photo du recto du document.</Text>
            </View>
          </Pressable>

          <Pressable style={styles.uploadOption} onPress={() => takePhoto('back')}>
            <Image source={require('../../../assets/camera.png')} style={styles.icon} />
            <View>
              <Text style={styles.optionTitle}>Prendre le verso</Text>
              <Text style={styles.optionHint}>Photo du verso du document.</Text>
            </View>
          </Pressable>

          <Pressable style={styles.uploadOption} onPress={pickPdfOrImage}>
            <Image source={require('../../../assets/file_upload.png')} style={styles.icon} />
            <View>
              <Text style={styles.optionTitle}>Importer un PDF complet</Text>
              <Text style={styles.optionHint}>PDF complet du document. Max 15MB.</Text>
            </View>
          </Pressable>
        </View>

        <FileRow file={frontFiles[0]} kind="front" />
        <FileRow file={backFiles[0]} kind="back" />
        <FileRow file={pdfFiles[0]} kind="pdf" />

        {error && <Text style={styles.error}>{error}</Text>}

        <Pressable
          onPress={() => {
            setSkip((v) => !v);
            setFrontFiles([]);
            setBackFiles([]);
            setPdfFiles([]);
          }}
          style={styles.skipRow}
        >
          <View style={[styles.skipBox, skip && styles.skipBoxOn]}>
            {skip ? <Text style={styles.skipMark}>✓</Text> : null}
          </View>
          <Text style={styles.skipLabel}>{t('documentUpload.skip')}</Text>
        </Pressable>

        <View style={styles.fileInfoHint}>
          <Text style={styles.fileInfoText}>📁 Taille maximale: 15MB</Text>
          <Text style={styles.fileInfoText}>
            📄 PDF accepté comme document complet. Images: recto et verso requis.
          </Text>
        </View>

        <Pressable
          onPress={handleNext}
          disabled={!hasCompleteDocument() || uploading}
          style={[
            styles.cta,
            (!hasCompleteDocument() || uploading) && { opacity: 0.5 },
          ]}
        >
          {uploading ? (
            <ActivityIndicator color="#FFF" />
          ) : (
            <Text style={styles.ctaText}>{t('documentUpload.next')}</Text>
          )}
        </Pressable>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: COLORS.background },
  scroll: {
    padding: 20,
    paddingBottom: 40,
    maxWidth: 520,
    width: '100%',
    alignSelf: 'center',
  },
  backButton: {
    width: 42,
    height: 42,
    borderRadius: 12,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
    marginTop: 16,
  },
  backIcon: { fontSize: 18, color: COLORS.textDark },
  title: {
    fontSize: 24,
    fontWeight: '800',
    color: COLORS.textDark,
    marginBottom: 6,
  },
  subtitle: {
    fontSize: 14,
    color: COLORS.textMuted,
    lineHeight: 20,
    marginBottom: 18,
  },
  card: {
    backgroundColor: COLORS.card,
    borderRadius: 18,
    padding: 14,
    gap: 12,
    borderWidth: 1,
    borderColor: COLORS.border,
    marginBottom: 16,
  },
  uploadOption: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
    padding: 14,
    borderRadius: 14,
    backgroundColor: '#F9FAFB',
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  icon: { width: 32, height: 32 },
  optionTitle: {
    fontWeight: '700',
    color: COLORS.textDark,
    fontSize: 15,
  },
  optionHint: {
    color: COLORS.textMuted,
    fontSize: 12,
    marginTop: 2,
  },
  fileRow: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: COLORS.border,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  fileName: { fontWeight: '600', color: COLORS.textDark },
  fileSize: { fontSize: 12, color: COLORS.textMuted, marginTop: 2 },
  remove: { color: COLORS.error, fontWeight: '700' },
  error: { color: COLORS.error, marginBottom: 12 },
  skipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 8,
    marginBottom: 16,
  },
  skipBox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: COLORS.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  skipBoxOn: {
    backgroundColor: COLORS.primary,
    borderColor: COLORS.primary,
  },
  skipMark: { color: '#fff', fontWeight: '900' },
  skipLabel: { color: COLORS.textDark, fontWeight: '600' },
  fileInfoHint: {
    backgroundColor: '#F3F4F6',
    padding: 12,
    borderRadius: 12,
    marginBottom: 16,
  },
  fileInfoText: {
    fontSize: 12,
    color: COLORS.textMuted,
    marginBottom: 4,
  },
  cta: {
    backgroundColor: COLORS.primary,
    borderRadius: 14,
    paddingVertical: 16,
    alignItems: 'center',
  },
  ctaText: {
    color: '#fff',
    fontWeight: '800',
    fontSize: 16,
  },
});